import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Paper,
  TextField,
  Tooltip,
} from "@mui/material";
import { InsertDriveFile, Delete } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { uploadFileAPI } from "../../../../services/rest/files/filesProvider";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const PDFUploader = ({ setLoading, selectedUser, handleClose }) => {
  const { _id } = useSelector((state) => state.userReducer);
  const [file, setFile] = useState(null);
  const [fileNameError, setFileNameError] = useState(false);

  const handleFileChange = useCallback((event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Verificar tamaño del archivo (10MB máximo)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("El archivo no puede ser mayor a 10MB");
        return;
      }

      if (selectedFile.type === "application/pdf") {
        setFile(selectedFile);
      } else {
        toast.error("Por favor, sube solo archivos PDF.");
      }
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setFileNameError(false);
  }, []);

  // Función para truncar el nombre del archivo si es muy largo
  const truncateFileName = (name) => {
    const maxLength = 20; // Ajusta este valor según el ancho de tu contenedor
    if (name.length > maxLength) {
      return `${name.substring(0, maxLength)}...`;
    }
    return name;
  };

  const uploadFile = async () => {
    try {
      setLoading(true);

      // Preparar los datos para la API
      const formData = new FormData();
      formData.append("file", file);
      formData.append("userId", selectedUser._id);

      // console.log("Datos a enviar a la API:", {
      //   userId: selectedUser._id,
      //   originalFileName: file.name,
      //   fileSize: file.size,
      // });

      // Aquí iría la llamada real a la API
      const response = await uploadFileAPI({
        id_provider: _id,
        id_patient: selectedUser?._id,
        formData,
      });
      // console.log(response);

      toast.success(
        `Archivo  subido con éxito para ${selectedUser?.firstName}`
      );
      handleClose();
    } catch (error) {
      // console.error("Error al subir archivo:", error);
      toast.error(
        "En este momento no se pudo subir el archivo. \n Intentalo más tarde."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 500, margin: "auto" }}>
      <Typography variant="h6" gutterBottom sx={{ marginTop: 2 }}>
        Subir Archivo PDF
      </Typography>

      <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
        <Button
          component="label"
          variant="contained"
          sx={{ backgroundColor: "#e7ef7d", mb: 2 }}
          startIcon={<InsertDriveFile />}
          disabled={!!file}
          fullWidth
        >
          Seleccionar PDF
          <VisuallyHiddenInput
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            disabled={!!file}
          />
        </Button>

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Solo se permite un archivo PDF (Máx. 10MB)
        </Typography>
      </Paper>

      {file && (
        <>
          <Paper elevation={3} sx={{ padding: 2, mb: 2 }}>
            <List>
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={handleRemoveFile}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <InsertDriveFile sx={{ color: "#e7ef7d" }} />
                </ListItemIcon>
                <Tooltip title={file.name} arrow>
                  <ListItemText
                    primary={truncateFileName(file.name)}
                    secondary={`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                  />
                </Tooltip>
              </ListItem>
            </List>
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleRemoveFile}
              sx={{ color: "text.secondary" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#e7ef7d" }}
              onClick={uploadFile}
              disabled={fileNameError}
            >
              Subir archivo
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PDFUploader;
